/**
 * Veluna Capture Guard - the single arbitration authority for intrusive
 * email capture.
 *
 * POLICY ONLY. It renders nothing, owns no UI, and stores no subscriber data.
 * Each capture engine keeps its own triggers, storage, cooldowns and submit
 * handler; this only answers one question: "may anything interrupt right now?"
 *
 * Replaces the reference to veluna-luna-decision-engine.js, which 404'd on
 * every page since the 2026-08-16 asset cleanup. That file defined
 * window.LunaDecisionEngine (three pure helper functions) and NOTHING in the
 * theme ever called it - restoring it would have restored a no-op. Retired.
 *
 * Rules implemented (deliberately only these):
 *   1. One intrusive overlay at a time.
 *   2. Purchase configuration on screen suppresses capture.
 *   3. A purchase action just taken suppresses capture.
 *   4. An email already captured suppresses further prompts.
 *   5. (2026-10-01) window.VelunaExitIntent, the shared exit-intent detector
 *      every capture engine subscribes to (block above start()).
 * Existing dismissal/frequency memory is untouched and still authoritative -
 * this guard can only ever say "no", never "yes".
 */
(function (w, d) {
  'use strict';
  if (w.VelunaCaptureGuard) return;

  /* Purchase-configuration blocks on the PDP and the quiz result page. */
  var COMMERCE_SEL = [
    '[data-veluna-purchase-type]',
    '[data-mr-purchase-type]',
    '.recovery-cream-hero__velunasub',
    '[data-velunasub-schedule]',
    '[data-velunasub-onetime]',
    '[data-mr-schedule]',
    /* 2026-09-23: the homepage set cards and the PDP add-to-cart are purchase
       controls too. A capture may not open while any of them is on screen. */
    '.vrs__buy',
    '.vrs__card',
    '.vrs__btn',
    '#recovery-cream-add-to-cart',
    '[data-veluna-atc]'
  ].join(',');

  /* Controls that mean "this visitor is transacting". */
  var PURCHASE_SEL = '[data-veluna-atc],#recovery-cream-add-to-cart,#mr-cta,' +
                     '#mr-v2-sticky-btn,#mr-cta-dup,#mr-v2-cta2-btn,' +
                     '[name="checkout"],a[href*="/checkout"]';

  /* Overlays owned by the capture engines, used for the one-at-a-time check.
     The unified-popup and the article/blog popup were previously invisible to
     this list, which is why the homepage could render two full-viewport capture
     overlays at once at every tested width. */
  var OVERLAY_SEL = '.vpm__overlay.is-open,.vqp__overlay.is-open,.mr-sheet.is-open,' +
                    '.luna-popup,[data-luna-popup],' +
                    '.unified-popup.unified-popup--visible,' +
                    '.veluna-fs-overlay.is-open,[data-veluna-article-popup].is-open,' +
                    '.veluna-exit-intent-modal--visible,.veluna-fs-popup.is-open,' +
                    '.vqx__overlay.is-open,[data-mcl-sheet]:not([hidden])';

  /* Arbitration priority when two engines become eligible in the same tick.
     Higher wins. Rule: a PAGE-SPECIFIC capture outranks the generic site-wide
     one, because it is the offer the visitor's current context earned.
     Equal priority falls back to first-to-acquire, which the owner lock already
     enforces. */
  var PRIORITY = {
    'pdp-mystery': 30,
    'quiz-results-exit': 30,
    'quiz-exit': 30,
    'article-capture': 20,
    'pdf-capture': 10,
    'unified-popup': 5
  };
  function rank(id) { return PRIORITY[id] != null ? PRIORITY[id] : 10; }

  /* PRECEDENCE, not preemption.
     A page-specific engine announces itself at load. The generic site-wide
     capture then stands down on that page instead of racing it to the guard.
     This is what makes the outcome deterministic: whichever engine happens to
     trigger first no longer decides which lane owns the page. */
  var pageSpecific = [];
  /* Engines may load before OR after this file, so the queue is read live on
     every query rather than drained once at init. Script order cannot change
     the outcome. */
  function allPageSpecific() {
    var out = pageSpecific.slice();
    try {
      (w.__vlnPageSpecific || []).forEach(function (id) {
        if (id && out.indexOf(id) === -1) out.push(id);
      });
    } catch (_) {}
    return out;
  }

  var PURCHASE_WINDOW_MS = 8000;

  /* 2026-09-23. Session memory the guard owns:
     - an add-to-cart this session ends all capture prompts for the session;
     - dismissing a capture ends THAT ENGINE's prompts for the session
       (each engine also writes its own 7-day key).
     2026-10-01 (owner): the dismissal is remembered per engine, key
     veluna_capture_dismissed_session:<id>. Closing the guide popup no longer
     silences the PDP offer and vice versa. The old single key
     'veluna_capture_dismissed_session' is no longer written or read. */
  var ATC_SESSION_KEY = 'veluna_atc_this_session';
  var DISMISS_SESSION_KEY = 'veluna_capture_dismissed_session';
  function dismissKey(id, surface) { return DISMISS_SESSION_KEY + ':' + (id || 'unknown') + (surface ? ':' + surface : ''); }  /* 2026-10-01: per-surface keys for engines that pass one */
  /* 2026-10-01: a capture older than this no longer silences the PDP offer. */
  var CAPTURE_RECENT_MS = 30 * 24 * 60 * 60 * 1000;
  var REOFFER_AFTER_MS = { 'pdp-mystery': CAPTURE_RECENT_MS };
  function ssGet(k) { try { return w.sessionStorage.getItem(k); } catch (_) { return null; } }
  function ssSet(k, v) { try { w.sessionStorage.setItem(k, v); } catch (_) {} }
  function markAddToCart() { ssSet(ATC_SESSION_KEY, String(Date.now())); }

  var commerceOnScreen = false;
  var lastPurchaseIntent = 0;
  var currentOwner = null;

  function onScreen(el) {
    if (!el) return false;
    var cs = w.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (w.innerHeight || 0) &&
           r.right > 0 && r.left < (w.innerWidth || 0) && r.width > 1 && r.height > 1;
  }

  /* Rule 2 - IntersectionObserver, not a timer: the page tells us where the
     visitor is. Re-scanned on DOM changes because the quiz result page builds
     its commerce block client-side after the recommendation resolves. */
  var observer = null;
  var watched = [];
  function watchCommerce() {
    if (!w.IntersectionObserver) return;
    var nodes = [].slice.call(d.querySelectorAll(COMMERCE_SEL));
    if (!nodes.length) return;
    if (!observer) {
      observer = new w.IntersectionObserver(function () {
        commerceOnScreen = watched.some(onScreen);
      }, { threshold: 0.15 });
    }
    nodes.forEach(function (n) {
      if (watched.indexOf(n) === -1) { watched.push(n); observer.observe(n); }
    });
    commerceOnScreen = watched.some(onScreen);
  }

  /* Rule 3 - capture purchase intent in the capture phase so it registers
     even when a handler stops propagation. */
  d.addEventListener('click', function (e) {
    try {
      if (e.target && e.target.closest && e.target.closest(PURCHASE_SEL)) {
        lastPurchaseIntent = Date.now();
        if (e.target.closest(ATC_SEL)) markAddToCart();
      }
    } catch (_) {}
  }, true);

  /* Add-to-cart, whatever the widget: a form posting to /cart/add, or any
     fetch/XHR to /cart/add (seen through Resource Timing, so nothing is
     wrapped or patched). */
  var ATC_SEL = '[data-veluna-atc],#recovery-cream-add-to-cart';
  d.addEventListener('submit', function (e) {
    try {
      var a = (e.target && e.target.getAttribute && e.target.getAttribute('action')) || '';
      if (a.indexOf('/cart/add') !== -1) markAddToCart();
    } catch (_) {}
  }, true);
  try {
    if (w.PerformanceObserver) {
      new w.PerformanceObserver(function (list) {
        list.getEntries().forEach(function (en) {
          if (en && typeof en.name === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(en.name.split('#')[0].replace(/^https?:\/\/[^/]+/, ''))) markAddToCart();
        });
      }).observe({ type: 'resource', buffered: true });
    }
  } catch (_) {}

  /* Rule 4 - read the engines' OWN existing keys. No new storage is created,
     so dismissal/frequency memory keeps working exactly as before. */
  function captureAgeMs() {
    /* veluna_pdf_captured = '<email>|<ms>' (every capture writes it). null = unknown age. */
    try {
      var ms = parseInt((localStorage.getItem('veluna_pdf_captured') || '').split('|')[1] || '0', 10);
      if (ms > 0) return Date.now() - ms;
    } catch (_) {}
    return null;
  }
  function alreadyCaptured(id) {
    var captured = false;
    try {
      if (localStorage.getItem('veluna_pdf_captured')) captured = true;
      else if (localStorage.getItem('veluna_email_captured') === 'true') captured = true;
      else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((localStorage.getItem('veluna_quiz_email') || '').trim())) captured = true;
    } catch (_) {}
    try {
      if (!captured && typeof w.hasEmailCaptured === 'function' && w.hasEmailCaptured()) captured = true;
    } catch (_) {}
    if (!captured) return false;
    /* 2026-10-01: the PDP offer may ask again once the capture is older than 30 days.
       An unknown age (legacy flags without a timestamp) counts as recent. */
    var reoffer = id && REOFFER_AFTER_MS[id];
    if (reoffer) { var age = captureAgeMs(); if (age !== null && age > reoffer) return false; }
    return true;
  }

  /* Rule 2, synchronous (2026-09-23): the IntersectionObserver registers
     after the engines' first checks, so it could report "not visible" while
     the buy box was on screen. mayShow() now measures right now. */
  function commerceVisibleNow() {
    var nodes = d.querySelectorAll(COMMERCE_SEL);
    for (var i = 0; i < nodes.length; i++) if (onScreen(nodes[i])) return true;
    return false;
  }

  /* Rule 1 - is any intrusive overlay already on screen? */
  function overlayOpen() {
    var nodes = d.querySelectorAll(OVERLAY_SEL);
    for (var i = 0; i < nodes.length; i++) if (onScreen(nodes[i])) return true;
    return false;
  }

  /* Priority of the highest-ranked overlay currently on screen. */
  function highestOpenRank() {
    var nodes = d.querySelectorAll(OVERLAY_SEL), best = 0;
    for (var i = 0; i < nodes.length; i++) {
      if (!onScreen(nodes[i])) continue;
      var owner = nodes[i].getAttribute('data-capture-id') || '';
      var r = owner ? rank(owner) : 10;
      if (r > best) best = r;
    }
    return best;
  }

  function suppressReason(id, surface) {
    if (alreadyCaptured(id)) return 'already_captured';
    /* Self-healing: an engine that closed its overlay without telling us must
       not be able to deadlock every other surface for the rest of the session. */
    if (currentOwner && !overlayOpen()) currentOwner = null;
    if (currentOwner && currentOwner !== id) return 'overlay_open:' + currentOwner;
    /* A lower-priority engine may not open on top of a higher-priority one that
       is already on screen, even if that engine never claimed ownership. */
    if (!currentOwner && overlayOpen() && rank(id) < highestOpenRank()) {
      return 'lower_priority_than_open_overlay';
    }
    if (overlayOpen()) return 'overlay_on_screen';
    if (ssGet(ATC_SESSION_KEY)) return 'added_to_cart_this_session';
    if (ssGet(dismissKey(id, surface))) return 'dismissed_this_session';
    commerceOnScreen = commerceVisibleNow();
    if (commerceOnScreen) return 'commerce_configuration_visible';
    if (Date.now() - lastPurchaseIntent < PURCHASE_WINDOW_MS) return 'purchase_in_progress';
    return null;
  }

  /* One shared modal helper for every capture overlay (.vqp, .vpm, .vqx).
     holdFocus(root, {focus, initial, onEscape}) -> release()
       - focuses the first control (or `initial`, a selector) unless focus:false
       - keeps Tab / Shift+Tab inside root
       - calls onEscape on Esc when given (engines that already close on Esc
         do not pass it)
       - while held, hides the price bar and puts the sticky header, trust
         strip and article quiz bar out of reach behind the overlay
       - release() restores focus to what had it before. */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
                  'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function injectChromeStyles() {
    if (d.getElementById('vln-capture-chrome')) return;
    var st = d.createElement('style');
    st.id = 'vln-capture-chrome';
    st.textContent =
      'html.vln-capture-open .vpb,html.vln-capture-open .vlcb-sticky{display:none!important;}' +
      'html.vln-capture-open #shopify-section-header,html.vln-capture-open .shopify-section-header,' +
      'html.vln-capture-open .veluna-header,html.vln-capture-open .veluna-cart-trust{pointer-events:none!important;}' +
      '.vqp__overlay,.vpm__overlay,.vqx__overlay{z-index:2147483000!important;}';
    (d.head || d.documentElement).appendChild(st);
  }
  var held = 0;
  function holdFocus(root, opts) {
    opts = opts || {};
    injectChromeStyles();
    var prev = d.activeElement;
    function visible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }
    function items() { return [].slice.call(root.querySelectorAll(FOCUSABLE)).filter(visible); }
    function onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') { if (opts.onEscape) { e.preventDefault(); opts.onEscape(); } return; }
      if (e.key !== 'Tab') return;
      var f = items();
      if (!f.length) { e.preventDefault(); return; }
      var first = f[0], last = f[f.length - 1], a = d.activeElement;
      if (e.shiftKey) {
        if (a === first || !root.contains(a)) { e.preventDefault(); last.focus(); }
      } else if (a === last || !root.contains(a)) { e.preventDefault(); first.focus(); }
    }
    d.addEventListener('keydown', onKey, true);
    held++;
    d.documentElement.classList.add('vln-capture-open');
    if (opts.focus !== false) {
      setTimeout(function () {
        var t = null;
        if (opts.initial) t = [].slice.call(root.querySelectorAll(opts.initial)).filter(visible)[0] || null;
        if (!t) t = items().filter(function (el) { return !el.hasAttribute('data-close') && !el.hasAttribute('data-vqx-close'); })[0] || items()[0];
        if (t) { try { t.focus({ preventScroll: true }); } catch (_) {} }
      }, 60);
    }
    var released = false;
    return function release() {
      if (released) return;
      released = true;
      d.removeEventListener('keydown', onKey, true);
      held = Math.max(0, held - 1);
      if (!held) d.documentElement.classList.remove('vln-capture-open');
      if (prev && prev.focus && d.contains(prev)) { try { prev.focus({ preventScroll: true }); } catch (_) {} }
    };
  }

  w.VelunaCaptureGuard = {
    /* Called at load by an engine that is specific to this page type. */
    registerPageSpecific: function (id) {
      if (id && pageSpecific.indexOf(id) === -1) pageSpecific.push(id);
    },
    /* True when this page has a dedicated capture, so the generic one yields. */
    hasPageSpecific: function (exceptId) {
      var all = allPageSpecific();
      for (var i = 0; i < all.length; i++) {
        if (all[i] !== exceptId) return true;
      }
      return false;
    },
    pageSpecific: function () { return allPageSpecific(); },
    /* The only method the engines need. Returns false = do not interrupt. */
    mayShow: function (id, surface) { return suppressReason(id, surface) === null; },
    reason: function (id, surface) { return suppressReason(id, surface); },
    notifyOpen: function (id) { currentOwner = id || 'unknown'; },
    notifyClosed: function (id) { if (!id || currentOwner === id) currentOwner = null; },
    /* A visitor said no: no more prompts from THAT engine for the rest of the session. */
    notifyDismissed: function (id, surface) { ssSet(dismissKey(id, surface), String(Date.now())); },
    markAddToCart: markAddToCart,
    holdFocus: holdFocus,
    commerceVisible: function () { return commerceOnScreen; },
    /* Exposed for QA/diagnostics only. */
    state: function () {
      return {
        commerceOnScreen: commerceVisibleNow(),
        addedToCartThisSession: !!ssGet(ATC_SESSION_KEY),
        dismissedThisSession: (function () {
          var m = {};
          ['pdf-capture', 'pdp-mystery', 'quiz-exit', 'mc-lp-sheet', 'quiz-results-exit'].forEach(function (id) { var v = ssGet(dismissKey(id)); if (v) m[id] = v; });
          return m;
        })(),
        captureAgeMs: captureAgeMs(),
        purchaseIntentAgoMs: lastPurchaseIntent ? Date.now() - lastPurchaseIntent : null,
        currentOwner: currentOwner,
        overlayOpen: overlayOpen(),
        alreadyCaptured: alreadyCaptured(),
        pageSpecific: allPageSpecific()
      };
    }
  };

  /* =====================================================================
     VelunaExitIntent (2026-10-01). One shared exit-intent detector for every
     capture engine (guide popup, PDP offer, quiz exit, Mind Calm sheet).
     The old per-engine rule fired only when the pointer left the window
     through the top edge; the owner's verdict was "very very weak".

     window.VelunaExitIntent.listen(fn, opts) -> unsubscribe()
       fn(kind, detail) is called with kind:
         'top'        pointer leaves the window with clientY < 120 (mouseout
                      with no relatedTarget, or mouseleave on <html>)
         'predict'    pointer inside the top 160 px moving upward faster
                      than 0.35 px/ms over the last ~120 ms
         'side'       pointer leaves through a side or the bottom (clientY
                      >= 120); only after opts.minSideMs on the page. A
                      pointer over a classic scrollbar also reports no
                      relatedTarget, so that case is confirmed after 700 ms
                      without any movement or scroll.
         'keyboard'   Cmd/Ctrl + L, T or W (address bar, new tab, close)
         'tab-return' the tab was hidden for at least 1 s and is visible again
         'back'       Back button (OFF unless opts.back is true): one history entry
                      is pushed when the first listener asks; the visitor's first
                      Back press lands on it and fires once per page load, the
                      next Back leaves. Transparent when nothing opens: if no
                      listener returns true (and no engine claimed the guard),
                      or an overlay is already open, the helper calls
                      history.back() itself so the press is never swallowed.
         'scroll-up'  phones: more than 120 px back up (cumulative) after
                      30 % depth
         'idle'       phones: 8 s without a scroll or touch after 30 % depth
       Rules the helper enforces for everyone: at most one callback per kind
       every 2 s; top/predict/side/keyboard only with a fine pointer that can
       hover; scroll-up/idle only with a coarse pointer; nothing at all while
       a capture overlay is on screen (the guard's own overlay check); each
       listener's opts.minMs (default 0) before any kind, opts.minSideMs
       (default 6000) before 'side', and opts.kinds (array) to opt out of
       kinds. Engines keep their own shouldShow()/guardAllows() decision.
       Engines that run before this file queue on window.__vlnExitQueue
       ([fn, opts] pairs); the queue is drained here and then forwards. */
  var EXIT_DEFAULTS = {
    minMs: 0, minSideMs: 6000, back: false, kinds: null,
    topY: 120, predictY: 130, predictSpeed: 0.6, predictWindowMs: 120,
    sideConfirmMs: 700, tabAwayMs: 1000, perKindMs: 2000,
    depth: 0.2, scrollUpPx: 120, idleMs: 6000   /* owner 2026-10-01 max leads (was 0.3 / 8000) */
  };
  var EXIT_KINDS = ['top', 'predict', 'side', 'keyboard', 'tab-return', 'back', 'scroll-up', 'idle'];
  var FINE_KINDS = { top: 1, predict: 1, side: 1, keyboard: 1 };
  var COARSE_KINDS = { 'scroll-up': 1, idle: 1 };
  var exitListeners = [];
  var exitLast = {};
  var exitLoadedAt = Date.now();
  var exitInstalled = false, backInstalled = false;
  var mqFine = null, mqCoarse = null;
  try { mqFine = w.matchMedia('(hover: hover) and (pointer: fine)'); mqCoarse = w.matchMedia('(pointer: coarse)'); } catch (_) {}
  function fineNow() { return !!(mqFine && mqFine.matches); }
  function coarseNow() { return !!(mqCoarse && mqCoarse.matches); }

  /* Returns true when a listener took the signal: it returned true, or an engine claimed
     the guard (notifyOpen) while the listeners ran. The 'back' trap relies on this. */
  function exitFire(kind, detail) {
    var now = Date.now();
    if (exitLast[kind] && now - exitLast[kind] < EXIT_DEFAULTS.perKindMs) return false;
    if (FINE_KINDS[kind] && !fineNow()) return false;
    if (COARSE_KINDS[kind] && !coarseNow()) return false;
    if (overlayOpen()) return false;
    var any = false, handled = false, ownerBefore = currentOwner;
    for (var i = 0; i < exitListeners.length; i++) {
      var L = exitListeners[i];
      if (L.kinds && L.kinds.indexOf(kind) === -1) continue;
      if (kind === 'back' && !L.opts.back) continue;
      if (now - exitLoadedAt < L.opts.minMs) continue;
      if (kind === 'side' && now - exitLoadedAt < L.opts.minSideMs) continue;
      any = true;
      try { if (L.fn(kind, detail || {}) === true) handled = true; } catch (_) {}
    }
    if (any) exitLast[kind] = now;
    if (!handled && currentOwner && currentOwner !== ownerBefore) handled = true;
    return handled;
  }

  function installExitDetectors() {
    if (exitInstalled) return;
    exitInstalled = true;
    var de = d.documentElement;

    /* top + side (fine pointer). */
    var sideTimer = null;
    function cancelSide() { if (sideTimer) { clearTimeout(sideTimer); sideTimer = null; } }
    function onOut(e, viaLeave) {
      if (!viaLeave && e.relatedTarget) return;
      var x = e.clientX, y = e.clientY;
      if (y < EXIT_DEFAULTS.topY) { exitFire('top', { x: x, y: y }); return; }
      if (viaLeave) return;
      var overScrollbar = (x >= de.clientWidth && x < (w.innerWidth || 0)) ||
                          (y >= de.clientHeight && y < (w.innerHeight || 0));
      if (overScrollbar) {
        cancelSide();
        sideTimer = setTimeout(function () { sideTimer = null; exitFire('side', { x: x, y: y, via: 'scrollbar' }); }, EXIT_DEFAULTS.sideConfirmMs);
        return;
      }
      exitFire('side', { x: x, y: y });
    }
    d.addEventListener('mouseout', function (e) { onOut(e, false); });
    de.addEventListener('mouseleave', function (e) { onOut(e, true); });
    ['mousemove', 'mousedown', 'wheel'].forEach(function (ev) { d.addEventListener(ev, cancelSide, { passive: true }); });
    w.addEventListener('scroll', cancelSide, { passive: true });

    /* predict (fine pointer): a fast upward move inside the top band. */
    var samples = [];
    d.addEventListener('mousemove', function (e) {
      var now = (w.performance && w.performance.now) ? w.performance.now() : Date.now();
      samples.push({ t: now, y: e.clientY });
      while (samples.length && now - samples[0].t > EXIT_DEFAULTS.predictWindowMs + 30) samples.shift();
      if (samples.length > 40) samples.splice(0, samples.length - 40);
      if (e.clientY >= EXIT_DEFAULTS.predictY || samples.length < 2) return;
      var o = samples[0], dt = now - o.t, dy = o.y - e.clientY;
      if (dt < 16 || dy < 24) return;
      if (dy / dt > EXIT_DEFAULTS.predictSpeed) {
        /* 2026-10-01 (owner: popups must not block navigation): never treat a move onto the site's own header, menu, link or button as an exit. */
        var over = null; try { over = document.elementFromPoint(e.clientX, e.clientY); } catch (_) {}
        if (over && over.closest && over.closest('header, nav, [role="navigation"], a, button, .vl-annc, .vlcb-sticky, .vpb, .vlcb-capture')) return;
        exitFire('predict', { y: e.clientY, speed: Math.round(dy / dt * 100) / 100 });
      }
    }, { passive: true });

    /* keyboard (fine pointer): capture on window so a page handler cannot eat it. */
    w.addEventListener('keydown', function (e) {
      if (!(e.metaKey || e.ctrlKey)) return;
      var k = String(e.key || '').toLowerCase();
      if (k === 'l' || k === 't' || k === 'w') exitFire('keyboard', { key: k });
    }, true);

    /* tab-return (every device): hidden for at least 1 s, then visible again. */
    var hiddenAt = 0;
    d.addEventListener('visibilitychange', function () {
      if (d.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (d.visibilityState !== 'visible') return;
      var away = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = 0;
      if (away >= EXIT_DEFAULTS.tabAwayMs) exitFire('tab-return', { awayMs: away });
    });

    /* scroll-up + idle (coarse pointer). Depth counts the viewport, like the engines. */
    var reached = false, upAcc = 0, idleTimer = null;
    var lastY = w.scrollY || de.scrollTop || 0;
    function depthNow() { return ((w.scrollY || de.scrollTop || 0) + (w.innerHeight || 0)) / (de.scrollHeight || 1); }
    function armIdle() {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { idleTimer = null; if (reached) exitFire('idle', {}); }, EXIT_DEFAULTS.idleMs);
    }
    w.addEventListener('scroll', function () {
      var y = w.scrollY || de.scrollTop || 0;
      if (depthNow() >= EXIT_DEFAULTS.depth) reached = true;
      if (y < lastY) upAcc += lastY - y; else if (y > lastY) upAcc = 0;
      lastY = y;
      if (reached && upAcc > EXIT_DEFAULTS.scrollUpPx) { upAcc = 0; exitFire('scroll-up', { y: y }); }
      armIdle();
    }, { passive: true });
    d.addEventListener('touchstart', armIdle, { passive: true });
    d.addEventListener('touchmove', armIdle, { passive: true });
    if (depthNow() >= EXIT_DEFAULTS.depth) reached = true;
    armIdle();
  }

  /* back (2026-10-01, owner): one history entry that the visitor's first Back press consumes.
     Off by default; the first listener with opts.back arms it.
       - The entry carries state {vlnExit: 1} (merged with whatever state the page had), so a
         popstate that lands ON it (Forward, or Back from an in-page anchor pushed above it)
         is ignored; only the pop to the entry below counts, and only once per page load.
       - Reloading onto the trap entry (history.state.vlnExit) re-arms on that entry instead
         of pushing a second one.
       - The entry below is marked scrollRestoration 'manual' before the push so Back does
         not jump to where the page was when the entry was made (the top); the trap entry
         itself gets the page's previous mode back, and the handler sets 'auto' again after
         the pop and re-applies the last known scroll position.
       - Transparent when nothing opens: an overlay already on screen, or every listener
         refusing (shouldShow / minMs), means history.back() right away. */
  var backArmed = false, backY = 0;
  function installBackTrap() {
    if (backInstalled) return;
    backInstalled = true;
    var H = w.history;
    try {
      if (H.state && H.state.vlnExit) {
        backArmed = true;
      } else {
        var prevMode = H.scrollRestoration || 'auto';
        try { H.scrollRestoration = 'manual'; } catch (_) {}
        var st = {};
        try { var cur = H.state; if (cur && typeof cur === 'object') for (var k in cur) if (Object.prototype.hasOwnProperty.call(cur, k)) st[k] = cur[k]; } catch (_) {}
        st.vlnExit = 1;
        H.pushState(st, '', w.location.href);
        try { H.scrollRestoration = prevMode; } catch (_) {}
        backArmed = true;
      }
    } catch (_) { return; }
    backY = w.scrollY || d.documentElement.scrollTop || 0;
    w.addEventListener('scroll', function () { if (backArmed) backY = w.scrollY || d.documentElement.scrollTop || 0; }, { passive: true });
    w.addEventListener('popstate', function (e) {
      if (!backArmed) return;
      if (e.state && e.state.vlnExit) return;
      backArmed = false;
      try { H.scrollRestoration = 'auto'; } catch (_) {}
      try { w.scrollTo(0, backY); } catch (_) {}
      var leave = overlayOpen() || !exitFire('back', {});
      if (leave) { try { H.back(); } catch (_) {} }
    });
  }

  function exitListen(fn, opts) {
    if (typeof fn !== 'function') return function () {};
    var o = {};
    for (var k in EXIT_DEFAULTS) if (Object.prototype.hasOwnProperty.call(EXIT_DEFAULTS, k)) o[k] = EXIT_DEFAULTS[k];
    if (opts) for (var j in opts) if (Object.prototype.hasOwnProperty.call(opts, j) && opts[j] != null) o[j] = opts[j];
    var L = { fn: fn, opts: o, kinds: (o.kinds && o.kinds.length) ? o.kinds.slice() : null, at: Date.now() };
    exitListeners.push(L);
    installExitDetectors();
    if (o.back) installBackTrap();
    return function () {
      var i = exitListeners.indexOf(L);
      if (i !== -1) exitListeners.splice(i, 1);
    };
  }

  w.VelunaExitIntent = {
    listen: exitListen,
    kinds: EXIT_KINDS.slice(),
    defaults: EXIT_DEFAULTS,
    state: function () {
      return { listeners: exitListeners.length, last: exitLast, fine: fineNow(), coarse: coarseNow(),
               installed: exitInstalled, back: backInstalled, backArmed: backArmed, overlayOpen: overlayOpen(),
               sincePageMs: Date.now() - exitLoadedAt };
    }
  };
  /* Engines whose inline script ran before this file (quiz, Mind Calm lander). */
  try {
    var queued = w.__vlnExitQueue;
    if (queued && queued.length) for (var qi = 0; qi < queued.length; qi++) exitListen(queued[qi][0], queued[qi][1]);
  } catch (_) {}
  w.__vlnExitQueue = { push: function (item) { try { exitListen(item[0], item[1]); } catch (_) {} } };

  function start() {
    watchCommerce();
    if (w.MutationObserver && d.body) {
      var debounce;
      new w.MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(watchCommerce, 250);
      }).observe(d.body, { childList: true, subtree: true });
    }
    w.addEventListener('scroll', function () {
      commerceOnScreen = watched.some(onScreen);
    }, { passive: true });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start);
  else start();
})(window, document);
